import { useState } from 'react';
import { MedplumClient } from '@medplum/core';
import { Bundle, Invoice, ServiceRequest, Specimen } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { ARKON } from '@consts/systems.consts';
import { validateOrderForm, isValidOrderCode } from '../utils/order-validation';
import { generateOrderCode } from '../utils/code-generation-util';
import {
  createServiceRequestFromForm,
  uploadOrderDocumentsAndGetRefs,
} from '../utils/fhir-helpers';
import { linkSpecimenToOrder } from '../utils/specimen-workflow';
import { buildInvoiceForOrder } from '../utils/payment-helpers';
import { OrderFormData, OrderFormErrors } from '../components/OrderForm';

interface UseCreateOrderParams {
  medplum: MedplumClient;
  formData: OrderFormData;
  setFormErrors: (errors: OrderFormErrors) => void;
  selectedSpecimens: Specimen[];
  encounterId?: string;
}

interface UseCreateOrderResult {
  loading: boolean;
  createOrder: () => Promise<ServiceRequest | null>;
}

async function buildServiceRequest(
  medplum: MedplumClient,
  formData: OrderFormData,
  orderId: string,
  encounterId: string | undefined,
  selectedSpecimens: Specimen[]
): Promise<ServiceRequest> {
  const serviceRequest = createServiceRequestFromForm(
    formData,
    orderId,
    'active',
    encounterId
  );

  if (selectedSpecimens.length > 0) {
    serviceRequest.specimen = selectedSpecimens.map((specimen) => ({
      reference: `Specimen/${specimen.id}`,
      display: specimen.identifier?.[0]?.value || specimen.id,
    }));
  }

  const supportingInfoRefs = await uploadOrderDocumentsAndGetRefs(
    medplum,
    formData,
    orderId
  );
  if (supportingInfoRefs.length > 0) {
    serviceRequest.supportingInfo = supportingInfoRefs;
  }

  return serviceRequest;
}

function buildTransactionBundle(
  serviceRequest: ServiceRequest,
  formData: OrderFormData,
  bundleUrn: string
): Bundle {
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      {
        fullUrl: bundleUrn,
        resource: serviceRequest,
        request: { method: 'POST', url: 'ServiceRequest' },
      },
    ],
  };

  if (!formData.patient) {
    return bundle;
  }

  // Temporarily assign the bundle URN as ID so getReferenceString works inside buildInvoiceForOrder
  const tempSr: ServiceRequest = { ...serviceRequest, id: bundleUrn };
  const draftInvoice: Invoice = buildInvoiceForOrder(
    tempSr,
    formData.patient,
    [],
    0,
    0,
    formData.testCode,
    formData.testDisplay
  );

  // Patch the linked-order reference to point directly to the URN
  const linkedOrderExt = draftInvoice.extension?.find(
    (e) => e.url === ARKON.SD.LINKED_ORDER
  );
  if (linkedOrderExt?.valueReference) {
    linkedOrderExt.valueReference.reference = bundleUrn;
  }

  // Patch the subject reference to use the bare URN so the FHIR server
  // resolves it to "ServiceRequest/{realId}" instead of the double-prefixed
  // "ServiceRequest/ServiceRequest/{realId}" that getReferenceString produces.
  if (draftInvoice.subject) {
    draftInvoice.subject.reference = bundleUrn;
  }

  bundle.entry = [
    ...(bundle.entry ?? []),
    { resource: draftInvoice, request: { method: 'POST', url: 'Invoice' } },
  ];

  return bundle;
}

async function linkSpecimens(
  medplum: MedplumClient,
  orderId: string,
  selectedSpecimens: Specimen[]
): Promise<void> {
  await Promise.all(
    selectedSpecimens.map((specimen) =>
      linkSpecimenToOrder(medplum, specimen.id || '', orderId).catch(
        (error) => {
          console.warn(
            `Failed to link specimen ${specimen.id} to order:`,
            error
          );
        }
      )
    )
  );
}

export function useCreateOrder({
  medplum,
  formData,
  setFormErrors,
  selectedSpecimens,
  encounterId,
}: UseCreateOrderParams): UseCreateOrderResult {
  const { t } = useTranslation(['lims', 'common']);
  const [loading, setLoading] = useState(false);

  const createOrder = async (): Promise<ServiceRequest | null> => {
    const validation = validateOrderForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return null;
    }

    setLoading(true);

    try {
      const orderId = generateOrderCode();
      if (!isValidOrderCode(orderId)) {
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: 'Invalid order code format generated. Please try again.',
          color: 'red',
        });
        return null;
      }

      const serviceRequest = await buildServiceRequest(
        medplum,
        formData,
        orderId,
        encounterId,
        selectedSpecimens
      );

      const bundleUrn = 'urn:uuid:' + crypto.randomUUID();
      const bundle = buildTransactionBundle(
        serviceRequest,
        formData,
        bundleUrn
      );

      const bundleResponse = await medplum.executeBatch(bundle);
      const createdServiceRequest = bundleResponse.entry?.[0]?.resource as
        | ServiceRequest
        | undefined;

      if (!createdServiceRequest) {
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: t('orderDetails.notifications.unexpectedError'),
          color: 'red',
        });
        return null;
      }

      if (createdServiceRequest.id && selectedSpecimens.length > 0) {
        await linkSpecimens(
          medplum,
          createdServiceRequest.id,
          selectedSpecimens
        );
      }

      notifications.show({
        title: t('success', { ns: 'common' }),
        message:
          selectedSpecimens.length > 0
            ? t('orderCreate.notifications.createSuccessWithSpecimens', {
                orderId,
                count: selectedSpecimens.length,
              })
            : t('orderCreate.notifications.createSuccess', { orderId }),
        color: 'green',
      });

      return createdServiceRequest;
    } catch (error: any) {
      notifications.show({
        title: t('orderCreate.notifications.createError'),
        message:
          error.message || t('orderDetails.notifications.unexpectedError'),
        color: 'red',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { loading, createOrder };
}
